import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Upload, MapPin, Calendar, Briefcase, UserRound, CheckCircle, Zap } from 'lucide-react';
import QuickSearchDialog from './request-form/QuickSearchDialog';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { useAuth } from '@/providers/AuthProvider';
import { useDebounce } from '@/hooks/useDebounce';
import { useSettlements, Settlement } from '@/hooks/useSettlements';
import { FcGoogle } from "react-icons/fc";
import { createRequest } from '@/services/requests';
import { supabase } from '@/integrations/supabase/client';
import ProfessionSelect, { type ProfessionOptionLabels } from './request-form/ProfessionSelect';
import { fetchProfessionSupply } from '@/services/professions/supply';
import { keepWithSupply } from '@/utils/professionSupply';
import CitySelect from './request-form/CitySelect';
import ImageUpload from './request-form/ImageUpload';
import TimingPicker from './request-form/TimingPicker';
import ContactAuthForm from './request-form/ContactAuthForm';
import ServiceSelect from './request-form/ServiceSelect';
import { 
  initializeProfessionsCacheNew, 
  getProfessionOptionsForForm,
  getProfessionLabelNew,
  getMainProfessionForSpecialization,
  getSpecializationLabel,
  getProfessionById
} from '@/services/professions/getProfessionsNew';
import { 
  ProfessionService, 
  getServicesForSpecialization, 
  getServicesForProfession 
} from '@/services/professions/getServices';
import { useDirection } from '@/providers/DirectionProvider';
import { useTranslateToHebrew, isHebrewText } from '@/hooks/useTranslation';
import { getLabel } from '@/utils/textUtils';
import { requirePhoneVerification } from '@/utils/requirePhoneVerification';
import { markOAuthIntent, clearOAuthIntent, consumeOAuthIntent, OAUTH_INTENT_MAX_AGE_MS } from '@/utils/oauthIntent';
import { REQUEST_FORM_STORAGE_KEY } from '@/utils/storedRequestForm';

interface RequestFormProps {
  onSuccess?: (value: boolean) => void;
  initialProfession?: string | null;
}

// Storage key for form persistence during OAuth flow
const FORM_STORAGE_KEY = REQUEST_FORM_STORAGE_KEY;

interface StoredFormData {
  formData: {
    profession: string;
    description: string;
    location: string;
    timing: string;
    contactName: string;
    phone: string;
    email: string;
  };
  coordinates: {
    latitude: number | null;
    longitude: number | null;
  };
  selectedDate: string | null;
  selectedService: {
    id: string;
    label: string;
  } | null;
  timestamp: number;
  // Saved right before the Google redirect from the send step. It goes out by itself on the way back only if this flag
  // is set, the form is under 10 minutes old and the same-tab marker left by that click is still there (see oauthIntent)
  autoSubmit?: boolean;
}

const saveFormToStorage = (data: StoredFormData) => {
  try {
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving form to localStorage:', error);
  }
};

const loadFormFromStorage = (): StoredFormData | null => {
  try {
    const stored = localStorage.getItem(FORM_STORAGE_KEY);
    console.log('📦 loadFormFromStorage - stored data exists:', !!stored);
    if (!stored) return null;
    
    const data = JSON.parse(stored) as StoredFormData;
    console.log('📦 loadFormFromStorage - parsed data:', { 
      formData: data.formData, 
      hasCoordinates: !!(data.coordinates?.latitude),
      timestamp: new Date(data.timestamp).toISOString()
    });
    
    // Check if data is not older than 1 hour
    if (Date.now() - data.timestamp > 60 * 60 * 1000) {
      console.log('📦 loadFormFromStorage - data expired, removing');
      localStorage.removeItem(FORM_STORAGE_KEY);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error loading form from localStorage:', error);
    return null;
  }
};

const clearFormStorage = () => {
  try {
    localStorage.removeItem(FORM_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing form storage:', error);
  }
};
// Overwrite sanitizeFilename for even safer results and more detailed logging.
const sanitizeFilename = (filename: string) => {
  // Split name and extension
  const dotIdx = filename.lastIndexOf(".");
  let base = dotIdx > -1 ? filename.substring(0, dotIdx) : filename;
  let ext = dotIdx > -1 ? filename.substring(dotIdx) : "";

  // Remove all non-ASCII alphanumeric, -, or _, translate Hebrew/Unicode to "_"
  base = base.normalize('NFD') // Decompose unicode for more consistency
  .replace(/[\u0590-\u05FF]/g, "_") // Convert Hebrew utf-8 chars to "_"
  .replace(/[\u0600-\u06FF]/g, "_") // Arabic (if ever needed)
  .replace(/[^\w\-]/g, "_") // Non-word
  .replace(/_+/g, "_").replace(/^-+|-+$/g, "").toLowerCase();

  // If the extension is not valid ascii, clean up or fallback.
  ext = ext.replace(/[^.\w]/g, "").toLowerCase();
  if (!ext.startsWith(".")) ext = ".png";
  if (!/^\.\w+$/.test(ext)) ext = ".png";

  // Avoid empty name
  if (base.length < 2) base = "img";
  return base + ext;
};

// ✅ פונקציה להמרת תת-התמחות למקצוע ראשי (לקטגוריה)
const getProfessionForRequest = (selectedProfession: string): string => {
  const mainProf = getMainProfessionForSpecialization(selectedProfession);
  if (mainProf) {
    // אם נבחרה תת-התמחות → מחזירים את המקצוע הראשי
    return mainProf.label; // "אינסטלטור"
  }

  // גיבוי למקצועות - get label
  return getProfessionLabelNew(selectedProfession) || selectedProfession;
};

// ✅ פונקציה ליצירת ה-Title ההיררכי: שירות > התמחות > מקצוע
const getRequestTitle = (
  selectedProfession: string, 
  selectedService: ProfessionService | null
): string => {
  // 1. אם נבחר שירות → השירות (בעברית)
  if (selectedService?.label) {
    return selectedService.label;
  }
  
  // 2. אם נבחרה תת-התמחות → שם ההתמחות בלבד
  const mainProf = getMainProfessionForSpecialization(selectedProfession);
  if (mainProf) {
    const specLabel = getSpecializationLabel(selectedProfession);
    if (specLabel) {
      return specLabel; // "פתיחת סתימות" (ללא שם המקצוע הראשי)
    }
  }
  
  // 3. אחרת → המקצוע הראשי
  return getProfessionLabelNew(selectedProfession) || selectedProfession;
};
const RequestForm: React.FC<RequestFormProps> = ({
  onSuccess,
  initialProfession
}) => {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    direction
  } = useDirection();
  const {
    translateFields,
    isTranslating
  } = useTranslateToHebrew();
  const [step, setStep] = useState(1);
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [newRequestId, setNewRequestId] = useState<string>("");
  const [openProfessionPopover, setOpenProfessionPopover] = useState(false);
  const [openCityPopover, setOpenCityPopover] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [professionSearchTerm, setProfessionSearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coordinates, setCoordinates] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({
    latitude: null,
    longitude: null
  });

  // Load profession options asynchronously from database (with static translations)
  const [allProfessionOptions, setAllProfessionOptions] = useState<Array<{
    id: string;
    label: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
    type: 'main' | 'sub' | 'all';
    mainProfessionId?: string;
    searchTerms?: string[];
  }>>([]);
  // A trade from the URL that has no professionals yet: shown as a notice instead of being selected
  const [unavailableProfession, setUnavailableProfession] = useState<ProfessionOptionLabels | null>(null);
  useEffect(() => {
    const loadProfessions = async () => {
      const [options, supplied] = await Promise.all([getProfessionOptionsForForm(), fetchProfessionSupply()]);
      // Filter out "all" option - request form requires specific profession selection
      const specific = options.filter(opt => opt.type !== 'all');
      const filteredOptions = keepWithSupply(specific, supplied);
      if (initialProfession && !filteredOptions.some(o => o.id === initialProfession)) {
        setUnavailableProfession(specific.find(o => o.id === initialProfession) ?? null);
      }
      setAllProfessionOptions(filteredOptions);
    };
    loadProfessions();

    // Prefetch QuickSearch chunk in the background so the dialog opens instantly
    import('@/pages/QuickSearch').catch(() => {});
  }, []);

  // Service selection state
  const [selectedService, setSelectedService] = useState<ProfessionService | null>(null);
  const [availableServices, setAvailableServices] = useState<ProfessionService[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Use static translations from database (no dynamic translation needed)
  const [filteredProfessions, setFilteredProfessions] = useState(allProfessionOptions);

  // Use settlements hook for dynamic city fetching (with static translations from DB)
  const {
    settlements,
    loading: settlementsLoading
  } = useSettlements(citySearchTerm);

  // Get the display name for the selected city based on current language
  const getSelectedCityDisplayName = (): string | undefined => {
    if (!formData.location) return undefined;
    const selectedSettlement = settlements.find(s => s.name === formData.location);
    if (!selectedSettlement) return formData.location;
    // Use getLabel to get translated name based on current language
    if (i18n.language === 'en' && selectedSettlement.name_en) return selectedSettlement.name_en;
    if (i18n.language === 'fr' && selectedSettlement.name_fr) return selectedSettlement.name_fr;
    if (i18n.language === 'ru' && selectedSettlement.name_ru) return selectedSettlement.name_ru;
    return selectedSettlement.name;
  };
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    user,
    signIn,
    signUp,
    signInWithGoogle,
    checkPhoneVerification,
    phoneVerified
  } = useAuth();
  const debouncedProfessionSearch = useDebounce(professionSearchTerm, 300);
  const debouncedCitySearch = useDebounce(citySearchTerm, 300);
  const [formData, setFormData] = useState({
    profession: '',
    description: '',
    location: '',
    timing: '',
    contactName: '',
    phone: '',
    email: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    emailConfirm: '',
    password: '',
    passwordConfirm: '',
    agreeTerms: false,
    phone: ''
  });
  
  // Get location state for pending request submission
  const routeLocation = useLocation();
  const submitPendingRequest = routeLocation.state?.submitPendingRequest;
  
  // Restore form when user returns from Google OAuth or phone verification
  const [hasRestoredFromOAuth, setHasRestoredFromOAuth] = useState(false);
  // The visitor asked to send this request: they signed up or logged in from the form, or pressed send
  // before verifying their phone. It goes out by itself once they are signed in with a verified phone.
  const [hasPendingSubmit, setHasPendingSubmit] = useState(false);
  // isSubmitting only changes on the next render, so two triggers in the same tick (auto-submit and a
  // tap on the button) would both get through. The lock is taken synchronously and kept after success.
  const submitLockRef = useRef(false);
  // The request is in; the page navigates to the dashboard shortly. The button stays disabled until then.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  useEffect(() => {
    const storedData = loadFormFromStorage();
    console.log('🔄 Form restore check - user:', !!user, 'storedData:', !!storedData, 'hasRestoredFromOAuth:', hasRestoredFromOAuth, 'isSubmitting:', isSubmitting, 'submitPendingRequest:', submitPendingRequest);
    
    if (storedData && !hasRestoredFromOAuth && !isSubmitting) {
      // Restore form data regardless of user state
      console.log('✅ Restoring form data:', storedData.formData);
      setFormData(storedData.formData);
      setCoordinates(storedData.coordinates);
      if (storedData.selectedDate) {
        setSelectedDate(new Date(storedData.selectedDate));
      }
      // Restore selected service if saved
      if (storedData.selectedService) {
        console.log('✅ Restoring selected service:', storedData.selectedService);
        setSelectedService(storedData.selectedService as ProfessionService);
      }
      
      // Only mark as restored and clear storage if user is authenticated
      if (user) {
        console.log('✅ User authenticated - marking as restored and clearing storage');
        setHasRestoredFromOAuth(true);
        clearFormStorage();
        // Read (and delete) the marker the Google click left, whatever happens next
        const cameBackFromGoogle = consumeOAuthIntent('form');
        const autoSubmitAfterGoogle = cameBackFromGoogle && storedData.autoSubmit === true
          && Date.now() - storedData.timestamp < OAUTH_INTENT_MAX_AGE_MS;
        
        // Check if this is a return from phone verification - auto-submit
        if (submitPendingRequest || autoSubmitAfterGoogle) {
          console.log('✅ Pending request flag detected - will auto-submit');
          setHasPendingSubmit(true);
        } else if (!hasPendingSubmit) {
          // Show toast that form was restored - user can now add images (not when they already asked to send it)
          toast({
            title: t('requestForm.formRestored', { defaultValue: 'הטופס שוחזר בהצלחה' }),
            description: t('requestForm.formRestoredDescription', { 
              defaultValue: 'אפשר להוסיף תמונות ולהגיש את הבקשה' 
            }),
          });
        }
      }
    }
  }, [user, hasRestoredFromOAuth, isSubmitting, submitPendingRequest, t, toast]);
  
  // Auto-submit once the phone is verified: right after a login with an already verified phone, or when the
  // verification modal succeeds (it flips phoneVerified, which re-runs this check against the database).
  useEffect(() => {
    if (!hasPendingSubmit || !user || isSubmitting || !formData.profession || !formData.location) return;
    let cancelled = false;
    checkPhoneVerification().then(verified => {
      if (cancelled || !verified) return;
      console.log('🚀 Auto-submitting pending request');
      setHasPendingSubmit(false);
      handleFormSubmit();
    });
    return () => { cancelled = true; };
  }, [hasPendingSubmit, user, phoneVerified, isSubmitting, formData.profession, formData.location]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactName: user.user_metadata?.name || prev.contactName || '',
        email: user.email || prev.email || '',
        phone: user.user_metadata?.phone || prev.phone || ''
      }));
    }
  }, [user]);

  // After successful registration/OTP, return to step 1 (request form) so user can add images and submit
  useEffect(() => {
    if (user && step === 2) {
      console.log('User authenticated while on step 2, returning to step 1');
      setStep(1);
    }
  }, [user, step]);

  // Dynamic filtering of professions by search (label, id, searchTerms)
  useEffect(() => {
    const search = debouncedProfessionSearch?.trim().toLowerCase();
    if (!search) {
      setFilteredProfessions(allProfessionOptions);
      return;
    }
    setFilteredProfessions(allProfessionOptions.filter(p => {
      const inLabel = p.label.toLowerCase().includes(search);
      const inId = p.id.toLowerCase().includes(search);
      const inTerms = (p as any).searchTerms?.some((term: string) => term.toLowerCase().includes(search));
      return inLabel || inId || inTerms;
    }));
  }, [debouncedProfessionSearch, allProfessionOptions]);

  // Update filteredProfessions when translations load
  useEffect(() => {
    if (allProfessionOptions.length > 0 && !debouncedProfessionSearch) {
      setFilteredProfessions(allProfessionOptions);
    }
  }, [allProfessionOptions]);

  // Set initial profession from URL parameter
  useEffect(() => {
    if (initialProfession && allProfessionOptions.length > 0 && !formData.profession) {
      // Check if the profession exists in our options
      const professionExists = allProfessionOptions.some(p => p.id === initialProfession);
      if (professionExists) {
        setFormData(prev => ({ ...prev, profession: initialProfession }));
      }
    }
  }, [initialProfession, allProfessionOptions]);

  // Load services when profession changes
  useEffect(() => {
    const loadServices = async () => {
      if (!formData.profession) {
        setAvailableServices([]);
        setSelectedService(null);
        return;
      }
      
      setServicesLoading(true);
      setSelectedService(null); // Reset when profession changes
      
      try {
        // Check if this is a specialization or a main profession
        const mainProf = getMainProfessionForSpecialization(formData.profession);
        
        let services: ProfessionService[] = [];
        if (mainProf) {
          // It's a specialization - get the specialization UUID
          // The formData.profession is the slug, we need to find the UUID
          const { data: specData } = await supabase
            .from('profession_specializations')
            .select('id')
            .eq('specialization_id', formData.profession)
            .single();
          
          if (specData?.id) {
            services = await getServicesForSpecialization(specData.id);
          }
        } else {
          // It's a main profession - get all services for all its specializations
          const prof = getProfessionById(formData.profession);
          if (prof?.id) {
            services = await getServicesForProfession(prof.id);
          }
        }
        
        setAvailableServices(services);
      } catch (error) {
        console.error('Error loading services:', error);
        setAvailableServices([]);
      } finally {
        setServicesLoading(false);
      }
    };
    
    loadServices();
  }, [formData.profession]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCitySelect = (settlement: Settlement) => {
    setFormData(prev => ({
      ...prev,
      location: settlement.name
    }));
    setCoordinates({
      latitude: settlement.latitude,
      longitude: settlement.longitude
    });
    console.log('City selected with coordinates:', settlement);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      const newImages = [...images, ...fileArray];
      setImages(newImages);
      const newPreviewUrls = fileArray.map(file => URL.createObjectURL(file));
      setPreviewUrls([...previewUrls, ...newPreviewUrls]);
    }
  };
  const removeImage = (index: number) => {
    const newImages = [...images];
    const newPreviewUrls = [...previewUrls];
    URL.revokeObjectURL(newPreviewUrls[index]);
    newImages.splice(index, 1);
    newPreviewUrls.splice(index, 1);
    setImages(newImages);
    setPreviewUrls(newPreviewUrls);
  };
  const handleSelectDate = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setFormData(prev => ({
        ...prev,
        timing: format(date, 'dd/MM/yyyy')
      }));
    }
    setOpenCalendar(false);
  };
  const handleNext = () => {
    if (isSubmitting || hasSubmitted || submitLockRef.current) return;
    const missingFields: string[] = [];
    if (!formData.profession) missingFields.push(t('requestForm.fields.profession'));
    if (!formData.location) missingFields.push(t('requestForm.fields.location'));
    if (!formData.timing) missingFields.push(t('requestForm.fields.timing'));
    if (missingFields.length > 0) {
      // Name what is missing: a generic "fill all fields" left visitors guessing which one
      toast({
        title: t('requestForm.fillAllFields'),
        description: t('requestForm.missingFieldsList', { fields: missingFields.join(', ') }),
        variant: "destructive"
      });
      return;
    }
    if (user) {
      handleFormSubmit();
    } else {
      setStep(2);
      // גלילה לראש הכרטיס
      setTimeout(() => {
        const cardElement = document.getElementById('service-request-form-title');
        if (cardElement) {
          cardElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };
  const handleFormSubmit = async () => {
    if (isSubmitting || submitLockRef.current) {
      console.log('Form already submitting, ignoring duplicate request');
      return;
    }
    
    if (!user) {
      console.error('No user found, cannot create request');
      toast({
        title: t('requestForm.errorSubmitting'),
        description: t('requestForm.pleaseLogin'),
        variant: "destructive"
      });
      return;
    }
    
    submitLockRef.current = true;
    let requestCreated = false;

    // Check if phone is verified before allowing submission
    const hasPhoneVerified = await checkPhoneVerification();
    console.log('[RequestForm] Phone verification check:', hasPhoneVerified);
    if (!hasPhoneVerified) {
      console.log('[RequestForm] Phone not verified, blocking submission');
      submitLockRef.current = false;
      // Send it as soon as the verification modal succeeds, without a second tap
      setHasPendingSubmit(true);
      requirePhoneVerification();
      toast({
        title: t('auth.phoneRequired'),
        description: t('auth.phoneRequiredDescription'),
        variant: "destructive"
      });
      // PhoneRequiredDialog stays closed after a dismiss, so ask for it again
      return;
    }
    
    setIsSubmitting(true);
    console.log('Submitting form data:', formData);
    try {
      let uploadedMediaUrls: string[] = [];
      if (images.length > 0) {
        for (const file of images) {
          // --- LOGGING FOR DEBUG ---
          console.log("Original filename:", file.name);
          const safeFileName = sanitizeFilename(file.name);
          console.log("Sanitized filename:", safeFileName);
          const fileName = `${user.id}/${Date.now()}_${safeFileName}`;
          console.log("Full upload file path:", fileName);
          const {
            data,
            error
          } = await supabase.storage.from("requests-media").upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });
          if (error) {
            console.error("Supabase upload error:", error);
            toast({
              title: t('requestForm.errorUploading'),
              description: error.message || file.name,
              variant: "destructive"
            });
          } else if (data && data.path) {
            const {
              data: publicUrlData
            } = supabase.storage.from("requests-media").getPublicUrl(data.path);
            if (publicUrlData && publicUrlData.publicUrl) {
              uploadedMediaUrls.push(publicUrlData.publicUrl);
              console.log("publicUrl saved to media_urls:", publicUrlData.publicUrl);
            } else {
              console.error("No publicUrl generated for file:", fileName, publicUrlData);
              toast({
                title: t('requestForm.imageAccessError'),
                description: t('requestForm.imageAccessErrorDescription'),
                variant: "destructive"
              });
            }
          } else {
            console.warn("No data returned from upload", data);
            toast({
              title: t('requestForm.generalUploadError'),
              description: t('requestForm.generalUploadErrorDescription', {
                filename: file.name
              }),
              variant: "destructive"
            });
          }
        }
      }
      console.log("Final array submitted to media_urls:", uploadedMediaUrls);

      // Check if description is in Hebrew or another language
      const descriptionIsHebrew = isHebrewText(formData.description);

      // Translate description to Hebrew if user typed in non-Hebrew (e.g., French)
      const translatedData = await translateFields({
        description: formData.description
      }, ['description']);

      // Determine what to save for original language support
      const descriptionHebrew = translatedData.description || formData.description;
      const descriptionOriginal = descriptionIsHebrew ? null : formData.description;
      const originalLanguage = descriptionIsHebrew ? null : i18n.language;

      // Convert timing keys to Hebrew for database storage
      const urgencyToHebrew: Record<string, string> = {
        'immediate': 'מיידי',
        'nextTwoDays': 'יומיים הקרובים',
        'nextWeek': 'בשבוע הקרוב',
        'upToMonth': 'עד חודש',
        'monthOrMore': 'חודש או יותר'
      };
      const timingHebrew = urgencyToHebrew[formData.timing] || formData.timing; // Use Hebrew or keep as-is (for dates)

      // Fetch the profession UUID from the database
      let professionUuid: string | null = null;
      if (formData.profession) {
        const { data: professionData } = await supabase
          .from('professions')
          .select('id')
          .eq('profession_id', formData.profession)
          .single();
        
        if (professionData?.id) {
          professionUuid = professionData.id;
        } else {
          // Try to find by specialization_id in profession_specializations
          const { data: specData } = await supabase
            .from('profession_specializations')
            .select('id')
            .eq('specialization_id', formData.profession)
            .single();
          
          if (specData?.id) {
            professionUuid = specData.id;
          }
        }
      }

      const requestId = await createRequest({
        title: getRequestTitle(formData.profession, selectedService),
        description: descriptionHebrew,
        description_original: descriptionOriginal || undefined,
        original_language: originalLanguage || undefined,
        location: formData.location,
        timing: timingHebrew,
        user_id: user.id,
        category: getProfessionForRequest(formData.profession),
        profession_id: professionUuid,
        media_urls: uploadedMediaUrls.length > 0 ? uploadedMediaUrls : undefined,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      });
      if (!requestId) {
        throw new Error('Failed to create request');
      }
      requestCreated = true;
      setHasSubmitted(true);
      setNewRequestId(requestId);
      setShowSuccessDialog(true);
      
      // Push event to dataLayer for analytics tracking
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': 'customer_upload_request'
        });
      }
      
      if (onSuccess) {
        onSuccess(false);
      }
      // Auto-navigate to dashboard after 2.5 seconds
      setTimeout(() => {
        setShowSuccessDialog(false);
        navigate('/dashboard');
      }, 2500);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: t('requestForm.errorSubmitting'),
        description: t('requestForm.errorSubmittingDescription'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      // After success the lock stays: the page navigates to the dashboard, and a second tap must not create a duplicate
      if (!requestCreated) submitLockRef.current = false;
    }
  };
  const handleGoogleSignIn = async () => {
    try {
      // Save form data before OAuth redirect
      saveFormToStorage({
        formData,
        coordinates,
        selectedDate: selectedDate ? selectedDate.toISOString() : null,
        selectedService: selectedService ? { id: selectedService.id, label: selectedService.label } : null,
        timestamp: Date.now(),
        autoSubmit: true
      });
      markOAuthIntent('form');
      
      // Show toast about images
      if (images.length > 0) {
        toast({
          title: t('requestForm.imagesNotSaved', { defaultValue: 'שים לב: התמונות לא ישמרו' }),
          description: t('requestForm.imagesNotSavedDescription', { defaultValue: 'לאחר ההתחברות תצטרך להעלות את התמונות מחדש' }),
          variant: "default"
        });
      }

      const {
        error
      } = await signInWithGoogle();
      if (error) {
        console.error('Error signing in with Google:', error.message);
        clearFormStorage(); // Clear on error
        clearOAuthIntent('form');
        toast({
          title: t('auth.loginError'),
          description: error.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error during Google sign in:', error);
      clearFormStorage(); // Clear on error
      clearOAuthIntent('form');
      toast({
        title: t('auth.loginError'),
        description: t('auth.loginError'),
        variant: "destructive"
      });
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!loginData.email || !loginData.password) {
      toast({
        title: t('requestForm.missingFields'),
        description: t('requestForm.fillAllFieldsDescription'),
        variant: "destructive"
      });
      return;
    }
    
    // Save form data before login (like OAuth flow) to prevent race condition
    saveFormToStorage({
      formData,
      coordinates,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      selectedService: selectedService ? { id: selectedService.id, label: selectedService.label } : null,
      timestamp: Date.now()
    });
    
    markOAuthIntent('form');   // the visitor asked to send this form: it may go out by itself after verification
    
    setIsSubmitting(true);
    setHasPendingSubmit(true);
    const {
      error
    } = await signIn(loginData.email, loginData.password);
    
    if (error) {
      setIsSubmitting(false);
      setHasPendingSubmit(false);
      // Don't clear form storage on error - user can retry. It stays for restoring only: no longer marked as "send it".
      clearOAuthIntent('form');
      
      // Check if it's a network error vs credentials error
      const isNetworkError = error.message?.includes('Failed to fetch') || 
                             error.message?.includes('network') ||
                             error.message?.includes('Network');
      
      if (isNetworkError) {
        toast({
          title: 'שגיאת רשת',
          description: 'בדוק את החיבור לאינטרנט ונסה שוב',
          variant: "destructive"
        });
      } else {
        toast({
          title: t('auth.loginError'),
          description: t('auth.checkCredentials'),
          variant: "destructive"
        });
      }
    } else {
      // Login successful - the auto-submit effect sends the request once the user and their verified phone are in
      setIsSubmitting(false);
    }
  };
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!registerData.name || !registerData.email || !registerData.password || !registerData.passwordConfirm) {
      toast({
        title: t('requestForm.missingFields'),
        description: t('requestForm.fillAllFieldsDescription'),
        variant: "destructive"
      });
      return;
    }
    
    // Email confirmation validation
    if (registerData.email !== registerData.emailConfirm) {
      toast({
        title: t('auth.emailMismatch'),
        description: t('auth.emailMismatchDescription'),
        variant: "destructive"
      });
      return;
    }
    
    if (registerData.password !== registerData.passwordConfirm) {
      toast({
        title: t('auth.passwordsNotMatch'),
        description: t('auth.passwordsNotMatchDescription'),
        variant: "destructive"
      });
      return;
    }
    if (!registerData.agreeTerms) {
      toast({
        title: t('auth.termsOfService'),
        description: t('auth.mustAgreeTerms'),
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    
    // Save form data to localStorage before registration (like Google OAuth flow)
    saveFormToStorage({
      formData,
      coordinates,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      selectedService: selectedService ? { id: selectedService.id, label: selectedService.label } : null,
      timestamp: Date.now()
    });
    
    markOAuthIntent('form');   // the visitor asked to send this form: it may go out by itself after verification
    
    // Save phone for verification popup
    const normalizedPhone = registerData.phone?.replace(/[-\s]/g, '') || '';
    if (normalizedPhone) {
      localStorage.setItem('pendingPhoneVerification', normalizedPhone);
    }
    
    setHasPendingSubmit(true);
    const {
      error
    } = await signUp(registerData.email, registerData.password, {
      name: registerData.name,
      phone: registerData.phone
    });
    
    if (error) {
      setIsSubmitting(false);
      setHasPendingSubmit(false);
      // Clear localStorage on error
      clearFormStorage();
      clearOAuthIntent('form');
      localStorage.removeItem('pendingPhoneVerification');
      toast({
        title: t('auth.registrationError'),
        description: t('auth.tryAgainLater'),
        variant: "destructive"
      });
    } else {
      // Registration successful - reset isSubmitting so form restore can work after verification
      setIsSubmitting(false);
    }
    // Don't call handleFormSubmit here - the phone verification modal comes first.
    // The auto-submit effect sends the request once the phone is verified.
  };
  const handleBack = () => {
    setStep(step - 1);
  };
  const handleGoToDashboard = () => {
    setShowSuccessDialog(false);
    navigate('/dashboard');
    setTimeout(() => {
      const element = document.getElementById('request-' + newRequestId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 500);
  };
  const renderFormContent = () => {
    if (step === 1) {
      return <div className="space-y-6 animate-fade-in">
          {/* Show restoration alert when user returned from OAuth */}
          {hasRestoredFromOAuth && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm font-medium">
                {t('requestForm.formRestoredAlert', { 
                  defaultValue: 'הטופס שוחזר בהצלחה! תוכל להוסיף תמונות ולשלוח את הבקשה.' 
                })}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProfessionSelect unavailable={unavailableProfession} profession={formData.profession} professions={allProfessionOptions} searchTerm={professionSearchTerm} filteredProfessions={filteredProfessions} openPopover={openProfessionPopover} setOpenPopover={setOpenProfessionPopover} setSearchTerm={setProfessionSearchTerm} onChange={handleSelectChange} />
            <CitySelect city={formData.location} cityDisplayName={getSelectedCityDisplayName()} settlements={settlements} searchTerm={citySearchTerm} loading={settlementsLoading} openPopover={openCityPopover} setOpenPopover={setOpenCityPopover} setSearchTerm={setCitySearchTerm} onSelect={handleCitySelect} />
          </div>
          
          {/* Service selection - only show when profession is selected and services are available */}
          {formData.profession && (availableServices.length > 0 || servicesLoading) && (
            <ServiceSelect
              services={availableServices}
              selectedService={selectedService}
              onSelect={setSelectedService}
              loading={servicesLoading}
            />
          )}
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-muted-foreground">{t('requestForm.detailedDescription')}</Label>
            <Textarea id="description" name="description" placeholder={t('requestForm.descriptionPlaceholder')} value={formData.description} onChange={handleInputChange} className="h-32 rounded-lg" />
          </div>
          <TimingPicker timing={formData.timing} openCalendar={openCalendar} setOpenCalendar={setOpenCalendar} selectedDate={selectedDate} handleSelectDate={handleSelectDate} handleInputChange={handleInputChange} />
          <ImageUpload previewUrls={previewUrls} onUpload={handleImageUpload} removeImage={removeImage} images={images} />
        </div>;
    } else {
      return <ContactAuthForm step={step} loginData={loginData} registerData={registerData} isSubmitting={isSubmitting} handleLoginChange={handleLoginChange} handleRegisterChange={handleRegisterChange} handleLogin={handleLogin} handleRegister={handleRegister} handleGoogleSignIn={handleGoogleSignIn} />;
    }
  };
  return <>
      <Card className="border-0 shadow-none bg-transparent overflow-hidden animate-fade-in-up w-full" aria-labelledby="service-request-form-title" dir={direction}>
        <div className="p-5 md:p-6 py-[16px]">
          <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
            <h2 id="service-request-form-title" className="text-xl font-bold text-brand-navy">
              <span className="text-brand-teal">{t('requestForm.sendingTitle')}</span> {t('requestForm.requestToProfessionals')}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => setShowQuickSearch(true)}
                className="bg-gradient-to-r from-brand-teal to-brand-teal-light hover:from-brand-teal-light hover:to-brand-teal text-white rounded-full px-4 py-2 h-auto text-sm font-semibold shadow-md shadow-brand-teal/30 hover:scale-105 transition-all duration-200 gap-1.5"
                size="sm"
              >
                <Zap className="h-4 w-4 fill-white" />
                {t('quickSearch.buttonLabel')}
              </Button>
              <div className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
                {user ? t('requestForm.step1of1') : t('requestForm.stepXofY', {
                step,
                total: 2
              })}
              </div>
            </div>
          </div>

          {renderFormContent()}

          <div className="flex justify-between mt-8">
            {step > 1 && <Button type="button" variant="outline" onClick={handleBack} className="border-brand-teal text-brand-teal hover:bg-brand-teal/10">
                {t('common.back')}
              </Button>}
            {step === 1 && <div className={direction === 'rtl' ? 'mr-auto' : 'ml-auto'}>
                <Button type="button" onClick={handleNext} disabled={isSubmitting || isTranslating || hasSubmitted} aria-busy={isSubmitting || hasSubmitted} className="bg-gradient-to-r from-brand-teal to-brand-teal-light hover:from-brand-teal-light hover:to-brand-teal text-white rounded-xl px-8 py-5 text-base font-semibold shadow-lg shadow-brand-teal/25 hover:shadow-xl hover:shadow-brand-teal/30 hover:scale-[1.02] transition-all duration-300">
                  {isSubmitting || isTranslating || hasSubmitted ? <span className="flex items-center gap-2">
                      <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {isTranslating ? t('common.translating') : t('common.sending')}
                    </span> : user ? t('requestForm.submitRequest') : t('common.continue')}
                </Button>
              </div>}
          </div>
        </div>
      </Card>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md" dir={direction}>
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2">
              <CheckCircle className="h-6 w-6 text-[#00D09E]" />
              <span>{t('requestForm.successTitle')}</span>
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('requestForm.successDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-4">
            <div className="mb-5 text-center">
              <p className="text-gray-700 mb-2">
                {t('requestForm.requestAddedToDashboard')}
              </p>
              <p className="text-gray-500 text-sm">
                {t('requestForm.trackStatusAndQuotes')}
              </p>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button type="button" className="bg-[#00D09E] hover:bg-[#00C090] text-white w-full" onClick={handleGoToDashboard}>
              {t('requestForm.goToDashboard')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickSearchDialog open={showQuickSearch} onOpenChange={setShowQuickSearch} />
    </>;
};
export default RequestForm;